import React, { useState, useEffect } from 'react';
import Countdown from 'react-countdown';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import API, { formatApiErrorDetail } from '../utils/axios';
import { buildAssetUrl } from '../contexts/SettingsContext';
import {
  Minus, Plus, Send, FileText, X, CheckCircle, Copy, Share2, Sparkles, Trophy, ShieldCheck, Gift,
} from 'lucide-react';

const StepperInput = ({ value, onChange, label, testId }) => {
  const num = value === '' || value == null ? 0 : parseInt(value);
  const dec = () => onChange(String(Math.max(0, num - 1)));
  const inc = () => onChange(String(Math.min(20, num + 1)));
  return (
    <div className="flex items-center justify-center gap-1" data-testid={testId}>
      <button type="button" onClick={dec} className="w-10 h-10 rounded-lg bg-[#1a2138] hover:bg-[#232b47] text-white flex items-center justify-center font-bold border border-white/10 transition" aria-label={`kurangi ${label}`}>
        <Minus size={16} />
      </button>
      <div className="w-14 h-10 flex items-center justify-center rounded-lg bg-[#0F1729] border border-white/10 text-white font-black text-lg tabular-nums">
        {num}
      </div>
      <button type="button" onClick={inc} className="w-10 h-10 rounded-lg bg-[#0f766e]/40 hover:bg-[#0f766e]/60 text-white flex items-center justify-center font-bold border border-teal-500/30 transition" aria-label={`tambah ${label}`}>
        <Plus size={16} />
      </button>
    </div>
  );
};

const CountBox = ({ label, value }) => (
  <div className="flex-1 min-w-0 rounded-xl bg-[#0F1729] border border-white/10 py-2 text-center">
    <div className="text-xl md:text-2xl font-black text-white tabular-nums leading-none">{String(value).padStart(2, '0')}</div>
    <div className="text-[9px] md:text-[10px] text-gray-400 mt-1 tracking-wider font-semibold">{label}</div>
  </div>
);

const countdownRenderer = ({ days, hours, minutes, seconds, completed }) => {
  const d = completed ? 0 : days;
  const h = completed ? 0 : hours;
  const m = completed ? 0 : minutes;
  const s = completed ? 0 : seconds;
  return (
    <div className="grid grid-cols-4 gap-1.5">
      <CountBox label="HARI" value={d} />
      <CountBox label="JAM" value={h} />
      <CountBox label="MENIT" value={m} />
      <CountBox label="DETIK" value={s} />
    </div>
  );
};

export const InlinePredictionCard = ({ match, websites, onTermsClick }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ website_id: '', user_id: '', home_score: '0', away_score: '0' });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPrizeModal, setShowPrizeModal] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);

  const homeName = match.home_team?.name || 'TBA';
  const awayName = match.away_team?.name || 'TBA';
  const homeLogo = buildAssetUrl(match.home_team?.logo);
  const awayLogo = buildAssetUrl(match.away_team?.logo);
  const isClosed = match.prediction_closed;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.website_id) { toast.error('Silakan pilih situs partner terlebih dahulu'); return; }
    if (!formData.user_id.trim()) { toast.error('User ID wajib diisi'); return; }
    setLoading(true);
    try {
      const { data } = await API.post('/predictions', {
        match_id: parseInt(match.id),
        website_id: parseInt(formData.website_id),
        user_id: formData.user_id,
        home_score: parseInt(formData.home_score) || 0,
        away_score: parseInt(formData.away_score) || 0,
      });
      setTicket(data);
      setShowSuccessModal(true);
      toast.success('Prediksi berhasil dikirim!');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Gagal mengirim prediksi');
    } finally { setLoading(false); }
  };

  return (
    <div className="match-card w-full sm:w-[380px] lg:flex-1 lg:max-w-[520px] lg:min-w-[440px] md:p-8" data-testid={`match-card-${match.id}`}>
      {/* League header */}
      <div className="text-center mb-4">
        <div className="text-[#FFD60A] font-semibold text-sm md:text-lg mb-1">{match.league}</div>
        {match.round && <div className="text-gray-500 text-xs md:text-sm">{match.round}</div>}
        <div className="mt-2">
          <span className={`inline-block text-[10px] font-bold px-3 py-1 rounded-full border ${!isClosed ? 'text-[#5eead4] border-teal-500/40 bg-teal-500/10' : 'text-gray-400 border-white/10 bg-white/5'}`}>
            {!isClosed ? '● PREDIKSI DIBUKA' : '● PREDIKSI DITUTUP'}
          </span>
        </div>
      </div>

      {/* Countdown */}
      {!isClosed && (
        <div className="mb-4">
          <Countdown date={new Date(match.kickoff)} renderer={countdownRenderer} />
        </div>
      )}

      {/* Kickoff */}
      <div className="text-center mb-4">
        <div className="text-[10px] md:text-xs text-gray-400 uppercase tracking-wider mb-1">Kickoff</div>
        <div className="text-white text-sm md:text-base font-semibold">
          {new Date(match.kickoff).toLocaleString('id-ID', { day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit', timeZone:'Asia/Jakarta'})} WIB
        </div>
        {match.venue && <div className="text-gray-500 text-xs mt-0.5">{match.venue}</div>}
      </div>

      {/* Teams */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-center flex-1">
          {homeLogo ? (
            <img src={homeLogo} alt={homeName} className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-2 object-contain rounded-full border-2 border-white/10" />
          ) : (
            <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-2 rounded-full bg-white/5 border-2 border-white/10 flex items-center justify-center"><span className="text-white/50 text-xl font-bold">{homeName?.[0]}</span></div>
          )}
          <div className="text-white font-bold text-xs md:text-sm">{homeName}</div>
          {match.match_status === 'finished' && match.home_score !== null && (
            <div className="text-2xl md:text-3xl font-black text-[#FFD60A] mt-1">{match.home_score}</div>
          )}
        </div>
        <div className="vs-badge mx-2">VS</div>
        <div className="text-center flex-1">
          {awayLogo ? (
            <img src={awayLogo} alt={awayName} className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-2 object-contain rounded-full border-2 border-white/10" />
          ) : (
            <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-2 rounded-full bg-white/5 border-2 border-white/10 flex items-center justify-center"><span className="text-white/50 text-xl font-bold">{awayName?.[0]}</span></div>
          )}
          <div className="text-white font-bold text-xs md:text-sm">{awayName}</div>
          {match.match_status === 'finished' && match.away_score !== null && (
            <div className="text-2xl md:text-3xl font-black text-[#FFD60A] mt-1">{match.away_score}</div>
          )}
        </div>
      </div>

      {/* Score Stepper */}
      {!isClosed && (
        <div className="rounded-xl bg-[#0F1729] border border-white/10 p-3 mb-3">
          <div className="text-center text-[10px] text-gray-400 uppercase tracking-wider mb-2">Tebak Skor</div>
          <div className="grid grid-cols-2 gap-3">
            <StepperInput value={formData.home_score} onChange={(v) => setFormData({...formData, home_score: v})} label={homeName} testId={`home-score-stepper-${match.id}`} />
            <StepperInput value={formData.away_score} onChange={(v) => setFormData({...formData, away_score: v})} label={awayName} testId={`away-score-stepper-${match.id}`} />
          </div>
        </div>
      )}

      {/* Prize card */}
      {match.total_prize && (
        <button
          type="button"
          onClick={() => setShowPrizeModal(true)}
          className="w-full rounded-xl bg-gradient-to-b from-[#FFD60A]/5 to-transparent border-2 border-[#FFD60A]/40 p-3 hover:border-[#FFD60A]/70 transition-all mb-3"
          data-testid={`prize-card-${match.id}`}
        >
          <div className="inline-flex items-center gap-1 bg-[#FFD60A]/10 border border-[#FFD60A]/30 text-[#FFD60A] text-[10px] font-bold px-3 py-0.5 rounded-full mb-2">
            <Trophy size={10} /> TOTAL HADIAH
          </div>
          <div className="flex items-center justify-center gap-2">
            <Sparkles size={14} className="text-[#FFD60A]" />
            <div className="text-[#FFD60A] text-xl md:text-2xl font-black tabular-nums">{match.total_prize}</div>
            <Sparkles size={14} className="text-[#FFD60A]" />
          </div>
          {match.prize_note && (
            <div className="flex items-center justify-center gap-1 text-[#5eead4] text-[10px] font-semibold mt-1">
              <ShieldCheck size={10} /> {match.prize_note}
            </div>
          )}
        </button>
      )}

      {/* Form */}
      {!isClosed ? (
        <form onSubmit={handleSubmit} className="space-y-3" data-testid={`prediction-form-${match.id}`}>
          <div>
            <label className="block text-white text-xs font-semibold mb-1.5">Nama Situs Partner</label>
            <select
              value={formData.website_id}
              onChange={(e) => setFormData({...formData, website_id: e.target.value})}
              required
              data-testid={`website-select-${match.id}`}
            >
              <option value="">Pilih situs partner</option>
              {(websites || []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-white text-xs font-semibold mb-1.5">User ID / Username</label>
            <input
              type="text"
              value={formData.user_id}
              onChange={(e) => setFormData({...formData, user_id: e.target.value})}
              placeholder="Contoh: AB12345LK"
              required
              data-testid={`user-id-input-${match.id}`}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 md:py-4 rounded-xl font-black text-white text-sm md:text-base bg-gradient-to-r from-[#0f766e] to-[#059669] hover:from-[#0d6864] hover:to-[#047857] transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid={`submit-prediction-btn-${match.id}`}
          >
            <Send size={16} />
            {loading ? 'Mengirim...' : `KIRIM TEBAK SKOR (${formData.home_score || 0} - ${formData.away_score || 0})`}
          </button>
          <button
            type="button"
            onClick={onTermsClick}
            className="w-full py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white text-[11px] md:text-xs font-bold uppercase flex items-center justify-center gap-2 border border-white/10"
            data-testid={`syarat-btn-${match.id}`}
          >
            <FileText size={12} /> Syarat &amp; Ketentuan
          </button>
        </form>
      ) : (
        <div className="space-y-2">
          <div className="w-full py-3 rounded-xl bg-gray-500/20 text-gray-500 text-center font-bold text-sm">
            Prediksi Ditutup
          </div>
          <button
            type="button"
            onClick={onTermsClick}
            className="w-full py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white text-[11px] md:text-xs font-bold uppercase flex items-center justify-center gap-2 border border-white/10"
          >
            <FileText size={12} /> Syarat &amp; Ketentuan
          </button>
        </div>
      )}

      {/* Prize Modal */}
      {showPrizeModal && (
        <div className="modal-overlay" onClick={() => setShowPrizeModal(false)} data-testid={`prize-modal-${match.id}`}>
          <div className="modal-content p-6 max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#FFD60A]/10 border border-[#FFD60A]/30 flex items-center justify-center shrink-0">
                  <Trophy className="text-[#FFD60A]" size={22} />
                </div>
                <div>
                  <div className="text-white font-black text-xl leading-tight">Rincian Hadiah Event</div>
                  <div className="text-gray-400 text-xs mt-1 tracking-wider font-semibold">{match.league}</div>
                </div>
              </div>
              <button onClick={() => setShowPrizeModal(false)} className="text-gray-400 hover:text-white shrink-0"><X size={22} /></button>
            </div>
            <div className="rounded-xl bg-gradient-to-b from-[#FFD60A]/5 to-transparent border-2 border-[#FFD60A]/40 p-5 text-center mb-5">
              <div className="inline-flex items-center gap-2 bg-[#FFD60A]/10 border border-[#FFD60A]/30 text-[#FFD60A] text-xs font-bold px-4 py-1 rounded-full mb-3">
                <Trophy size={12} /> TOTAL HADIAH UTAMA
              </div>
              <div className="text-[#FFD60A] text-4xl md:text-5xl font-black tabular-nums mb-2">{match.total_prize || '-'}</div>
              {match.prize_note && (
                <div className="flex items-center justify-center gap-1 text-[#5eead4] text-xs font-semibold">
                  <ShieldCheck size={12} /> {match.prize_note}{match.prize_verified && ' & RESMI'}
                </div>
              )}
            </div>
            {(match.prize_breakdown || []).length > 0 && (
              <>
                <div className="text-[#FFD60A] text-xs font-bold mb-3 tracking-wider flex items-center gap-2">
                  <Gift size={12} /> PEMBAGIAN HADIAH
                </div>
                <div className="space-y-2 mb-5">
                  {match.prize_breakdown.map((row, idx) => (
                    <div key={idx} className="flex items-center justify-between rounded-lg bg-[#0F1729] border border-white/10 px-4 py-3">
                      <span className="text-white text-sm">{row.label}</span>
                      <span className="text-[#FFD60A] font-black text-sm md:text-base tabular-nums">{row.amount}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="flex justify-end">
              <button onClick={() => setShowPrizeModal(false)} className="px-6 py-2.5 rounded-xl bg-[#FFD60A] hover:bg-[#FFC300] text-black font-bold text-sm transition">
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && ticket && (() => {
        const selectedWebsite = (websites || []).find(w => w.id === parseInt(formData.website_id));
        const shareUrl = `${window.location.origin}/check-ticket?code=${encodeURIComponent(ticket.ticket_code)}`;
        const shareText = `Cek tebakan skor saya di ${match.league}: ${homeName} ${formData.home_score} - ${formData.away_score} ${awayName}. Buka tiket: ${shareUrl}`;
        const doShare = async () => {
          if (navigator.share) {
            try { await navigator.share({ title: `Tebak Skor ${match.league}`, text: shareText, url: shareUrl }); } catch (_) {}
          } else {
            navigator.clipboard.writeText(shareUrl);
            toast.success('Link tebakan disalin ke clipboard');
          }
        };
        const closeModal = () => {
          setShowSuccessModal(false);
          setFormData({ website_id: '', user_id: '', home_score: '0', away_score: '0' });
          setTicket(null);
        };
        return (
          <div className="modal-overlay" data-testid={`success-modal-${match.id}`}>
            <div className="modal-content p-6 md:p-8 max-w-lg relative" onClick={(e) => e.stopPropagation()}>
              <button onClick={closeModal} className="absolute top-4 right-4 text-gray-400 hover:text-white">
                <X size={22} />
              </button>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-cyan-500/10 border-2 border-cyan-400/40 mb-4">
                  <CheckCircle className="text-cyan-400" size={44} />
                </div>
                <h2 className="text-2xl md:text-3xl font-black text-white mb-2">Tebakan Berhasil Dikirim!</h2>
                <p className="text-gray-400 text-sm">Prediksi Anda telah terdaftar dalam event ini.</p>
              </div>
              <div className="rounded-xl bg-[#0F1729] border border-white/10 overflow-hidden mb-4">
                <div className="text-center py-3 border-b border-white/10">
                  <div className="text-[#FFD60A] font-bold text-sm tracking-wider">
                    {match.league.toUpperCase()}{match.group_name ? ` - ${match.group_name.toUpperCase()}` : ''}
                  </div>
                </div>
                <div className="grid grid-cols-3 items-center py-5 px-4">
                  <div className="text-center">
                    <div className="text-white font-bold text-xs md:text-sm mb-1">{homeName.toUpperCase()}</div>
                    <div className="text-cyan-400 text-4xl md:text-5xl font-black tabular-nums">{formData.home_score || 0}</div>
                  </div>
                  <div className="text-center">
                    <div className="inline-block px-3 py-1 rounded-lg bg-white/5 border border-white/10 text-blue-400 font-black text-sm">VS</div>
                  </div>
                  <div className="text-center">
                    <div className="text-white font-bold text-xs md:text-sm mb-1">{awayName.toUpperCase()}</div>
                    <div className="text-cyan-400 text-4xl md:text-5xl font-black tabular-nums">{formData.away_score || 0}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 px-4 pb-4 pt-2 border-t border-white/5">
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Situs Partner:</div>
                    <div className="text-white font-bold text-sm">{selectedWebsite?.name || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs mb-1">User ID / Username:</div>
                    <div className="text-[#FFD60A] font-bold text-sm">{formData.user_id}</div>
                  </div>
                </div>
              </div>
              <div className="rounded-xl bg-[#FFD60A]/10 border border-[#FFD60A]/30 px-4 py-3 mb-5 flex items-start gap-2">
                <Sparkles className="text-[#FFD60A] shrink-0 mt-0.5" size={16} />
                <p className="text-[#FFD60A] text-xs md:text-sm">
                  Semoga beruntung! Pemenang akan diumumkan setelah pertandingan selesai.
                </p>
              </div>
              <div className="rounded-xl bg-[#0F1729] border border-white/10 p-4 mb-5">
                <div className="flex items-center gap-4">
                  {ticket.qr_code && (
                    <div className="bg-white p-2 rounded-lg shrink-0">
                      <QRCodeSVG value={shareUrl} size={96} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1 space-y-2">
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Nomor Tiket</div>
                      <div className="text-[#FFD60A] font-bold text-sm tabular-nums">{ticket.ticket_number}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Link Tiket (scan QR)</div>
                      <div className="flex items-center gap-1">
                        <code className="text-white font-mono text-[10px] md:text-xs bg-black/40 px-2 py-1 rounded flex-1 truncate">{shareUrl}</code>
                        <button
                          onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('Link disalin'); }}
                          className="text-[#FFD60A] hover:text-[#FFC300] p-1 shrink-0"
                          aria-label="Salin link"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={doShare} className="py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm flex items-center justify-center gap-2">
                  <Share2 size={16} /> Bagikan Tebakan
                </button>
                <button onClick={closeModal} className="py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black font-black text-sm">
                  Selesai
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
